import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../contexts/ClientContext';
import { useClientLimits } from '../../hooks/useClientLimits';
import clientService, { Client } from '../../services/clientService';
import { Crown } from 'lucide-react';

const ClientSelector: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient, setCurrentClient, setClientPermissions, refreshClients } = useClient();
  const { canCreateClient, currentCount, maxLimit } = useClientLimits();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Show simplified view if limit is 0 or 1 (single brand only)
  const showSimplified = maxLimit === 0 || maxLimit === 1;

  useEffect(() => {
    loadClients();
  }, []);

  // Expose refresh function globally for other components to use
  useEffect(() => {
    (window as any).refreshClientSelector = loadClients;
    return () => {
      delete (window as any).refreshClientSelector;
    };
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🔄 Loading clients...');
      
      const response = await clientService.getClients(1, 50); // Get first 50 clients
      setClients(response.clients);
      console.log(`📋 Loaded ${response.clients.length} clients:`, response.clients.map(c => `${c.name} (${c.id})`));
      
      // Set client as current based on localStorage or default to first
      const savedClientId = localStorage.getItem('current_client_id');
      console.log(`💾 Saved client ID from localStorage: ${savedClientId}`);
      console.log(`👤 Current client in context: ${currentClient?.name} (${currentClient?.id})`);
      
      if (response.clients.length > 0) {
        let clientToSwitch: Client;
        
        if (savedClientId) {
          // Try to find the saved client
          const savedClient = response.clients.find(c => c.id === savedClientId);
          if (savedClient) {
            clientToSwitch = savedClient;
            console.log(`✅ Restoring saved client: ${savedClient.name} (${savedClient.id})`);
          } else {
            // Saved client not found, use first available
            clientToSwitch = response.clients[0];
            console.log(`❌ Saved client not found, using first available: ${clientToSwitch.name}`);
          }
        } else {
          // No saved client, use first available
          clientToSwitch = response.clients[0];
          console.log(`🆕 No saved client, using first available: ${clientToSwitch.name}`);
        }
        
        // Only switch if no current client is set or if we found a different saved client
        const shouldSwitch = !currentClient || (savedClientId && currentClient.id !== savedClientId);
        console.log(`🔀 Should switch client? ${shouldSwitch} (no current: ${!currentClient}, different saved: ${savedClientId && currentClient?.id !== savedClientId})`);
        
        if (shouldSwitch) {
          console.log(`🔄 Switching to client: ${clientToSwitch.name}`);
          await switchToClient(clientToSwitch);
        } else {
          console.log(`⏭️ Skipping client switch - already on correct client`);
        }
      }
    } catch (err: any) {
      setError('Failed to load clients');
      console.error('❌ Failed to load clients:', err);
    } finally {
      setIsLoading(false);
      console.log('✅ Client loading completed');
    }
  };

  const switchToClient = async (client: Client) => {
    try {
      console.log(`Switching to client: ${client.name} (${client.id})`);
      const switchResponse = await clientService.switchClient(client.id);
      // Fetch full client details to ensure we have all fields including monthly_post_limit
      const fullClient = await clientService.getClient(client.id);
      setCurrentClient({
        ...fullClient,
        user_role: switchResponse.user_role,
      });
      setClientPermissions(switchResponse.permissions);
      localStorage.setItem('current_client_id', client.id);
      console.log(`Successfully switched to client: ${client.name} (${client.id})`);
      setIsOpen(false);
    } catch (err: any) {
      setError('Failed to switch client');
      console.error('Failed to switch client:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-black dark:text-gray-200">Loading clients...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="text-sm text-black dark:text-gray-200">
        No clients available
        <button 
          onClick={loadClients}
          className="ml-2 text-blue-600 hover:text-blue-700 underline"
        >
          Refresh
        </button>
      </div>
    );
  }

  // For simplified tiers, show just the brand name without dropdown
  if (showSimplified) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-black dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg">
        <div className="flex items-center space-x-2">
          {currentClient?.logo_url ? (
            <img
              src={currentClient.logo_url}
              alt={currentClient.name}
              className="w-5 h-5 rounded-full object-cover"
            />
          ) : (
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-blue-600">
                {currentClient?.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="truncate max-w-32">
            {currentClient?.name || 'My Brand'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-black dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <div className="flex items-center space-x-2">
          {currentClient?.logo_url ? (
            <img
              src={currentClient.logo_url}
              alt={currentClient.name}
              className="w-5 h-5 rounded-full object-cover"
            />
          ) : (
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-blue-600">
                {currentClient?.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="truncate max-w-32">
            {currentClient?.name || 'Select Client'}
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-64 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-[110]">
          <div className="py-1 max-h-64 overflow-y-auto">
            {clients.map((client) => (
              <button
                key={client.id}
                onClick={() => switchToClient(client)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-700 ${
                  currentClient?.id === client.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-black dark:text-gray-200'
                }`}
              >
                {client.logo_url ? (
                  <img
                    src={client.logo_url}
                    alt={client.name}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">
                      {client.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{client.name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                    {client.user_role}
                  </div>
                </div>
                {currentClient?.id === client.id && (
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          
          {!showSimplified && (
            <div className="border-t border-gray-200 dark:border-slate-600 py-1">
              <button
                onClick={() => {
                  if (canCreateClient) {
                    setIsOpen(false);
                    navigate('/app/clients/new');
                  } else {
                    // Just close dropdown, the button click will open the modal
                    setIsOpen(false);
                  }
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-white hover:text-white rounded-lg transition-all duration-200 cursor-pointer"
                style={{ 
                  background: 'linear-gradient(to right, #30b3dd, #4a298a)',
                  backgroundSize: '200% 100%',
                  backgroundPosition: 'left center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundPosition = 'right center';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundPosition = 'left center';
                }}
              >
                {canCreateClient ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                ) : (
                  <Crown className="w-5 h-5 text-yellow-300" />
                )}
                <span>
                  Create new brand
                  {!canCreateClient && ` (${currentCount}/${maxLimit})`}
                </span>
              </button>
            </div>
          )}
          

        </div>
      )}
    </div>
  );
};

export default ClientSelector;